import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FileUpload from "@/components/FileUpload";

export default function EMRPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([
      api.get("/medical-records"),
      api.get("/patients"),
    ]);
    setRecords(r.data); setPatients(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/medical-records", form);
      toast.success("Record saved");
      setOpen(false); setForm({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed (doctor or nurse only)");
    }
  };

  const canCreate = ["doctor", "nurse"].includes(user.role) || user.role === "super_admin";

  return (
    <div className="space-y-5 fade-up" data-testid="emr-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Medical Records</h1>
          <p className="text-sm text-muted-foreground mt-1">{records.length} clinical notes</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-record-btn"><Plus className="size-4" /> New record</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Add medical record</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="record-form">
                <div className="space-y-1.5">
                  <Label>Patient *</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Symptoms</Label>
                  <Textarea value={form.symptoms || ""} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Diagnosis</Label>
                  <Input value={form.diagnosis || ""} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Treatment plan</Label>
                  <Textarea value={form.treatment_plan || ""} onChange={(e) => setForm({ ...form, treatment_plan: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Follow-up</Label>
                  <Input value={form.follow_up || ""} onChange={(e) => setForm({ ...form, follow_up: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                {form.patient_id ? (
                  <div className="border-t border-border pt-3">
                    <FileUpload patientId={form.patient_id} resourceType="medical_record" label="Attach files" compact />
                  </div>
                ) : (
                  <div className="border-t border-border pt-3 text-[11px] text-muted-foreground">
                    Select a patient to attach files (reports, scans, PDFs).
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-record-btn">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {records.map((r) => {
          const patient = patients.find((p) => p.id === r.patient_id);
          return (
            <Card key={r.id}>
              <CardContent className="p-5">
                <div className="flex justify-between text-xs text-muted-foreground mono">
                  <span>{patient?.name || r.patient_id}</span>
                  <span>{new Date(r.created_at).toLocaleString()} • {r.doctor_name}</span>
                </div>
                {r.diagnosis && <div className="mt-2 font-semibold">{r.diagnosis}</div>}
                {r.symptoms && <div className="text-sm text-muted-foreground mt-1"><strong>Symptoms:</strong> {r.symptoms}</div>}
                {r.treatment_plan && <div className="text-sm text-muted-foreground mt-1"><strong>Plan:</strong> {r.treatment_plan}</div>}
                {r.notes && <div className="text-sm mt-2">{r.notes}</div>}
              </CardContent>
            </Card>
          );
        })}
        {records.length === 0 && (
          <Card><CardContent className="p-10 text-center text-muted-foreground flex flex-col items-center gap-3">
            <FileText className="size-8 opacity-50" />
            <div>No medical records yet.</div>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
